import { useState } from "react";
import { useTranslations } from "next-intl";
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword } from "firebase/auth";
import { firebaseAuth, googleProvider } from "@/lib/firebase";
import { useAuth } from "@/hooks/useAuth";
import { Link } from "@/i18n/navigation";

export default function Login() {
  const t = useTranslations("login");
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Get redirect URL from query params
  const redirectUrl = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search).get("redirect")
    : null;
  const getRoleRedirect = (role?: string) => role === "admin" ? "/dashboard" : role === "garage" ? "/garage" : "/mon-espace";
  const getPostLoginUrl = () => redirectUrl || getRoleRedirect(user?.role);

  // Already logged in → redirect
  if (!loading && user) {
    window.location.href = getPostLoginUrl();
    return null;
  }

  async function sendTokenToServer(idToken: string) {
    const res = await fetch("/api/auth/firebase", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
    if (!res.ok) throw new Error("Session creation failed");
    const data = await res.json();
    const defaultRedirect = data.role === "admin" ? "/dashboard" : data.role === "garage" ? "/garage" : "/mon-espace";
    window.location.href = redirectUrl || defaultRedirect;
  }

  async function handleGoogle() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken();
      await sendTokenToServer(idToken);
    } catch (err: any) {
      setError(err.message || "Google login failed");
      setSubmitting(false);
    }
  }

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      let result;
      if (isSignUp) {
        result = await createUserWithEmailAndPassword(firebaseAuth, email, password);
      } else {
        result = await signInWithEmailAndPassword(firebaseAuth, email, password);
      }
      const idToken = await result.user.getIdToken();
      await sendTokenToServer(idToken);
    } catch (err: any) {
      const msg = err.code === "auth/invalid-credential" ? t("errors.invalidCredential")
        : err.code === "auth/email-already-in-use" ? t("errors.emailInUse")
        : err.code === "auth/weak-password" ? t("errors.weakPassword")
        : err.message || t("errors.generic");
      setError(msg);
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="animate-pulse text-graphite/50">{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-offwhite px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2.5 justify-center mb-10">
          <div className="w-10 h-10 bg-swiss-red flex items-center justify-center">
            <span className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>H</span>
          </div>
          <span className="font-bold text-2xl tracking-tight text-graphite" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Helvetic<span className="text-swiss-red">Cars</span>
          </span>
        </Link>

        <div className="bg-white border border-border p-8">
          <h1 className="text-xl font-bold text-graphite mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {isSignUp ? t("signUpTitle") : t("loginTitle")}
          </h1>
          <p className="text-sm text-graphite/60 mb-6">
            {isSignUp ? t("signUpSubtitle") : t("loginSubtitle")}
          </p>

          {/* Google */}
          <button
            onClick={handleGoogle}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-3 border border-border px-4 py-3 text-sm font-medium text-graphite hover:bg-warm-gray transition-colors disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {t("continueWithGoogle")}
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-graphite/40 uppercase">{t("or")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Email/Password */}
          <form onSubmit={handleEmail} className="space-y-3">
            <input
              type="email"
              placeholder={t("emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full border border-border px-4 py-3 text-sm text-graphite placeholder:text-graphite/40 focus:outline-none focus:border-swiss-red"
            />
            <input
              type="password"
              placeholder={t("passwordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full border border-border px-4 py-3 text-sm text-graphite placeholder:text-graphite/40 focus:outline-none focus:border-swiss-red"
            />

            {error && (
              <p className="text-sm text-swiss-red">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-swiss-red text-white px-4 py-3 text-sm font-semibold hover:bg-swiss-red/90 transition-colors disabled:opacity-50"
            >
              {submitting ? t("submitting") : isSignUp ? t("signUpButton") : t("loginButton")}
            </button>
          </form>

          <p className="text-center text-sm text-graphite/60 mt-6">
            {isSignUp ? t("alreadyAccount") : t("noAccount")}
            {" "}
            <button
              onClick={() => { setIsSignUp(!isSignUp); setError(null); }}
              className="text-swiss-red font-medium hover:underline"
            >
              {isSignUp ? t("switchToLogin") : t("switchToSignUp")}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
